/*@jsxRuntime automatic @jsxImportSource react*/
import {jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {MainLayout} from '~/layouts/main';
export const meta = {
  title: 'OCP | В реальной жизни',
  description: 'Как принцип открытости-закрытости помогает решать задачи из реальной жизни, примеры и описание применения'
};
const MDXLayout = ({children}) => _jsx(MainLayout, {
  meta: meta,
  children: children
});
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      section: "section",
      h1: "h1",
      p: "p",
      h2: "h2",
      pre: "pre",
      code: "code",
      a: "a",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_components.section, {
      children: [_jsx(_components.h1, {
        children: "В реальной жизни"
      }), _jsx(_components.p, {
        children: "Принцип открытости-закрытости побуждает исследовать отношения между сущностями до того, как вы начнёте писать код. Это помогает выявлять ошибки проектирования на ранних этапах."
      }), _jsx(_components.p, {
        children: "Кроме этого OCP помогает отвязать модули друг от друга. Это минимизирует количество модулей, которые надо обновить, при изменении требований."
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Инъекция зависимостей и тестирование"
        }), _jsx(_components.p, {
          children: "При тестировании модулей, которые зависят от других модулей, разработчики могут столкнуться с проблемой, когда необходимо создать экземпляры каждой из зависимостей."
        }), _jsx(_components.p, {
          children: "Если модуль зависит от конкретной реализации другого, разработчикам придётся имитировать конкретную реализацию зависимости. Допустим, есть класс, который работает с хранилищем:"
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "class StorageService {\n  get(key: string): any {\n    return JSON.parse(localStorage.getItem(key))\n  }\n}\n"
          })
        }), _jsxs(_components.p, {
          children: ["Чтобы протестировать метод ", _jsx(_components.code, {
            children: "get"
          }), ", необходимо создать глобальный ", _jsx(_components.a, {
            href: "https://ru.wikipedia.org/wiki/Mock-%D0%BE%D0%B1%D1%8A%D0%B5%D0%BA%D1%82",
            children: "мок-объект"
          }), " ", _jsx(_components.code, {
            children: "localStorage"
          }), ". Такие объекты и переменные при тестировании могут привести к неправильной работе соседних тестов. (Например, если кто-то забыл сбросить ", _jsx(_components.code, {
            children: "localStorage"
          }), " после использования.)"]
        }), _jsxs(_components.p, {
          children: ["Если же мы привяжем зависимость не через конкретный объект, а через интерфейс, то получим возможность подменять зависимости на лету. Этот паттерн называется ", _jsx(_components.a, {
            href: "https://ru.wikipedia.org/wiki/%D0%92%D0%BD%D0%B5%D0%B4%D1%80%D0%B5%D0%BD%D0%B8%D0%B5_%D0%B7%D0%B0%D0%B2%D0%B8%D1%81%D0%B8%D0%BC%D0%BE%D1%81%D1%82%D0%B8",
            children: "инъекция зависимостей"
          }), " (Dependency injection, DI)."]
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "interface Storage {\n  getItem(key: string): any\n}\n\ninterface StorageDependencies {\n  storage: Storage\n}\n\nclass StorageService {\n  storage: Storage\n\n  // указываем, какие зависимости следует использовать\n  // JSON тоже стоит внедрять подобным образом,\n  // но для простоты примера берём в расчёт только localStorage\n  constructor({ storage = localStorage }: StorageDependencies) {\n    this.storage = storage\n  }\n\n  get(key: string): any {\n    // используем зависимость через интерфейс\n    return JSON.parse(this.storage.getItem(key))\n  }\n}\n"
          })
        }), _jsxs(_components.p, {
          children: ["Теперь при тестировании мы можем указать мок-объект для ", _jsx(_components.code, {
            children: "Storage"
          }), " локально. При этом нам не потребуется эмулировать работу объекта ", _jsx(_components.code, {
            children: "localStorage"
          }), " полностью. Нам достаточно описать метод ", _jsx(_components.code, {
            children: "getItem"
          }), ", работу которого мы и проверим. Например, используя ", _jsx(_components.a, {
            href: "https://jestjs.io",
            children: "Jest"
          }), ":"]
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "describe('StorageService', () => {\n  it('should return specified value', () => {\n    const mock: Storage = {\n      getItem: (key: string) => '42'\n    }\n\n    const service = new StorageService({storage: mock})\n    expect(service.get('test key')).toEqual(42)\n  })\n})\n"
          })
        }), _jsx(_components.p, {
          children: "Если нам важно проверить, вызвался ли правильный метод у зависимости, DI снова сделает решение задачи проще:"
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "describe('StorageService', () => {\n  it('should call now method of dependency object', () => {\n    const mock: Storage = {\n      getItem: jest.fn()\n    }\n\n    const service = new StorageService({storage: mock})\n    service.get('test key')\n\n    expect(mock.getItem).toHaveBeenCalled()\n  })\n})\n"
          })
        }), _jsxs(_components.p, {
          children: ["Такой подход удобен при ", _jsx(_components.a, {
            href: "https://bespoyasov.ru/ttt-tdd/",
            children: "разработке через тестирование"
          }), " (TDD). Он позволяет продумать API модуля заранее и продумать организацию зависимостей модулей друг от друга."]
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Инъекция зависимостей и расширение функциональности"
        }), _jsxs(_components.p, {
          children: ["Теперь представим, что в приложении появляются два места, где используются разные хранилища: ", _jsx(_components.code, {
            children: "localStorage"
          }), " в одном и ", _jsx(_components.code, {
            children: "verySophisticatedStorage"
          }), " в другом."]
        }), _jsxs(_components.p, {
          children: ["Если наш класс зависел напрямую от ", _jsx(_components.code, {
            children: "localStorage"
          }), ", у нас проблемы. При добавлении нового хранилища, нам придётся проверять, с каким из хранилищ мы имеем дело. А если API хранилищ сильно отличается, то код метода ", _jsx(_components.code, {
            children: "get"
          }), " сильно разрастётся из-за проверок и ", _jsx(_components.a, {
            href: "https://ru.wikipedia.org/wiki/%D0%90%D0%B4%D0%B0%D0%BF%D1%82%D0%B5%D1%80_(%D1%88%D0%B0%D0%B1%D0%BB%D0%BE%D0%BD_%D0%BF%D1%80%D0%BE%D0%B5%D0%BA%D1%82%D0%B8%D1%80%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D1%8F)",
            children: "адаптеров"
          }), "."]
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "class StorageService {\n  storageType: string\n\n  constructor(storageType: string) {\n    this.storageType = storageType\n  }\n\n  get(key: string): any {\n    if (this.storageType === 'verySophisticatedStorage') {\n      return verySophisticatedStorage.getByKey(key)\n    }\n\n    return JSON.parse(localStorage.getItem(key))\n  }\n}\n"
          })
        }), _jsxs(_components.p, {
          children: ["С другой стороны, если мы зависим от интерфейса, то метод ", _jsx(_components.code, {
            children: "get"
          }), " и конструктор класса ", _jsx(_components.code, {
            children: "StorageService"
          }), " не изменятся. Разное API хранилищ мы приведём к одному виду через адаптеры (отдельные новые сущности), которые будут реализовывать интерфейс ", _jsx(_components.code, {
            children: "Storage"
          }), "."]
        }), _jsx(_components.pre, {
          children: _jsx(_components.code, {
            className: "language-ts",
            children: "interface Storage {\n  getItem(key: string): any\n}\n\n// добавляем адаптер для verySophisticatedStorage;\n// он будет реализовывать интерфейс Storage,\n// поэтому его можно будет передать как зависимость для StorageService\nclass SophisticatedStorageAdapter implements Storage {\n  getItem(key: string): any {\n    return verySophisticatedStorage.getByKey(key)\n  }\n}\n\n// то же для localStorage\nclass LocalStorageAdapter implements Storage {\n  getItem(key: string): any {\n    return JSON.parse(localStorage.getItem(key))\n  }\n}\n\n// код класса StorageService не меняется!\nclass StorageService {\n  storage: Storage\n\n  constructor({ storage = localStorage }: StorageDependencies) {\n    this.storage = storage\n  }\n\n  get(key: string): any {\n    // вся реализация методов обоих хранилищ скрыта за адаптерами\n    return this.storage.getItem(key)\n  }\n}\n\n// работает как со старым хранилищем\nconst storageServiceWithLocalStorage = new StorageService({\n  storage: new LocalStorageAdapter()\n})\n\n// так и с новым\nconst storageServiceWithSophisticatedStorage = new StorageService({\n  storage: new SophisticatedStorageAdapter()\n})\n"
          })
        }), _jsxs(_components.p, {
          children: ["Таким образом новые бизнес-требования не затронут код уже созданного модуля ", _jsx(_components.code, {
            children: "StorageService"
          }), ", а будут внедрены через создание новых сущностей."]
        })]
      }), _jsxs(_components.section, {
        children: [_jsx(_components.h2, {
          children: "Материалы к разделу"
        }), _jsxs(_components.ul, {
          children: ["\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://ru.wikipedia.org/wiki/%D0%92%D0%BD%D0%B5%D0%B4%D1%80%D0%B5%D0%BD%D0%B8%D0%B5_%D0%B7%D0%B0%D0%B2%D0%B8%D1%81%D0%B8%D0%BC%D0%BE%D1%81%D1%82%D0%B8",
              children: "Инъекция зависимостей"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://ru.wikipedia.org/wiki/Mock-%D0%BE%D0%B1%D1%8A%D0%B5%D0%BA%D1%82",
              children: "Мок-объект"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://bespoyasov.ru/ttt-tdd/",
              children: "Разработка через тестирование"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://ru.wikipedia.org/wiki/%D0%90%D0%B4%D0%B0%D0%BF%D1%82%D0%B5%D1%80_(%D1%88%D0%B0%D0%B1%D0%BB%D0%BE%D0%BD_%D0%BF%D1%80%D0%BE%D0%B5%D0%BA%D1%82%D0%B8%D1%80%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D1%8F)",
              children: "Адаптер, шаблон"
            })
          }), "\n", _jsx(_components.li, {
            children: _jsx(_components.a, {
              href: "https://jestjs.io",
              children: "Jest"
            })
          }), "\n"]
        })]
      })]
    });
  }
}
export default MDXContent;
